import { InfoCircleOutlined } from "@ant-design/icons";
import { Button, Checkbox, Flex, Input, Modal, Tooltip } from "antd";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import {
  useAddOrderMutation,
  useCourseValidateMutation,
  useGetCoursesQuery,
  useGetPublicCourseDetailsQuery,
} from "../../../../app/features/courses/courses.api";
import { usePublicCheckoutMutation } from "../../../../app/features/order/order.api";
import type { IAddOrderDto } from "../../../../app/features/order/order.types";
import CustomTable from "../../../../components/CustomTable";
import { ENV_CONFIG } from "../../../../config/global.config";
import useAuth from "../../../../hooks/useAuth";
import useError from "../../../../hooks/useError";
import { toast } from "../../../../lib/toast";
import { errorToast } from "../../../../utils/error.utils";
import { cn } from "../../../../utils/global.utils";
import { commaNumber, enToBnNumber } from "../../../../utils/number.utils";
import SendIcon from "./SendIcon";

type AmountList = {
  discount: number;
  grandTotal: number;
  subtotal: number;
};

// update

export default function CheckoutDetails() {
  const location = useLocation();
  const courseId = location?.state?.courseId;
  const [checked, setChecked] = useState(false);
  const [reference, setReference] = useState("");
  const [showReferenceWarning, setShowReferenceWarning] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const referenceInputRef = useRef<any>(null);
  const { isAuthenticated, employee } = useAuth();
  const { data, isLoading, error } = useGetPublicCourseDetailsQuery(
    courseId as string,
    {
      skip: !courseId,
    },
  );
  useError(error, "কোর্স লোড করা যায়নি");
  const [couponCode, setCouponCode] = useState("");
  const [amountList, setAmountList] = useState<AmountList | undefined>(
    undefined,
  );

  const [addOrder, { isLoading: addOrderLoading }] = useAddOrderMutation();
  const [publicCheckout, { isLoading: publicCheckoutLoading }] =
    usePublicCheckoutMutation();

  const {
    data: coursesData,
    isLoading: coursesLoading,
    error: coursesError,
  } = useGetCoursesQuery({}, { skip: !isAuthenticated });
  useError(coursesError, "কোর্স লোড করা যায়নি");
  const [
    courseValidate,
    {
      isLoading: isCourseValidateLoading,
      error: courseValidateError,
      data: courseValidateData,
    },
  ] = useCourseValidateMutation();
  useError(courseValidateError, "কুপন কোড অ্যাপ্লাই করা যাচ্ছেনা");

  const isAlreadyBuy = !!coursesData?.find(
    (el) => el?.Course?.id === Number(courseId),
  );

  const courses = [
    {
      id: data?.id,
      name: data?.title,
      price: Number(data?.offerPrice || data?.price || 0),
      total: Number(data?.offerPrice || data?.price || 0),
    },
  ];

  const totalPrice = courses.reduce((acc, course) => acc + course.total, 0);
  const discount = amountList?.discount || 0;
  const grandTotal = amountList?.grandTotal || totalPrice;

  // Handle coupon validation success
  useEffect(() => {
    if (courseValidateData?.valid) {
      setAmountList({
        subtotal: courseValidateData.cart.subtotal,
        discount: courseValidateData.cart.discount,
        grandTotal: courseValidateData.cart.grandTotal,
      });
      toast("success", "কুপন কোড সফলভাবে প্রয়োগ করা হয়েছে");
    }
  }, [courseValidateData]);

  const processPayment = async () => {
    const redirectUrl = ENV_CONFIG.FRONTEND_URL + "/payment";

    const orderData: IAddOrderDto = {
      items: [
        {
          productType: "COURSE",
          productRefUuid: data?.uuid?.toString() || "",
          title: data?.title || "",
          productCode: "",
          unitPrice: Number(grandTotal || 0),
          quantity: 1,
          meta: {
            courseData: data,
            reference: reference || null,
            couponAppliedPrice: grandTotal,
          },
        },
      ],
      promotionCode: courseValidateData?.promotion?.code || undefined,
    };

    try {
      const res = await addOrder(orderData).unwrap();

      const checkoutRes = await publicCheckout({
        orderId: res?.id,
        orderNo: res?.orderNo,
        provider: "SSLCOMMERZ",
        currency: "BDT",
        // amountMinor: Number(res?.grandTotal || 0),
        amountMinor: Number(grandTotal || 0),
        snapshot: {
          // total: Number(res?.grandTotal || 0),
          total: Number(grandTotal || 0),
          customer: {
            name: employee?.fullName,
            email: employee?.email,
            phone: employee?.phone,
          },
          items: orderData?.items,
          ...res,
        },
        idempotencyKey: `chk_${Date.now()}_${Math.random() * 1000}`,
        redirectUrls: {
          success: `${redirectUrl}/success`,
          fail: `${redirectUrl}/failed`,
        },
        projectCode: "COURSE",
        userUuid: employee?.authUserId || "",
        reference,
      });

      const sslUrl = checkoutRes?.data?.redirectUrl;

      if (sslUrl) {
        window.location.href = sslUrl;
      }
    } catch (error: any) {
      errorToast(error, "কোর্স অর্ডার করা যায়নি");
    }
  };

  const handlePayment = async () => {
    if (!checked) {
      toast("error", "অনুগ্রহ করে শর্তাবলিতে সম্মতি প্রদান করুন");
      return;
    }

    if (!totalPrice) {
      toast("error", "Please add at least one API to the cart");
      return;
    }

    if (isAlreadyBuy) {
      toast("error", "আপনি ইতিমধ্যে এই কোর্সটি কিনেছেন");
      return;
    }

    // Check if reference is empty
    if (!reference.trim()) {
      setShowReferenceWarning(true);
      setShowConfirmModal(true);
      return;
    }

    // If reference is provided, proceed directly
    await processPayment();
  };

  const handleConfirmProceed = async () => {
    setShowConfirmModal(false);
    setShowReferenceWarning(false);
    await processPayment();
  };

  const handleCancelProceed = () => {
    setShowConfirmModal(false);
    // Focus the input field after modal closes
    setTimeout(() => {
      referenceInputRef.current?.focus();
      referenceInputRef.current?.select();
    }, 100);
  };

  const handleCouponCode = () => {
    if (data?.uuid && couponCode) {
      courseValidate({
        code: couponCode,
        items: [
          {
            productType: "COURSE",
            quantity: 1,
            productRefUuid: data?.uuid,
          },
        ],
      });
      setCouponCode("");
    }
  };

  const loading = isLoading || coursesLoading;

  return (
    <section className="mini-container pt-20">
      <div className="grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2">
          <h2 className="text-2xl font-bold font-roboto text-theme-slate-700">
            Course List
          </h2>
          <CustomTable
            rootClassName="p-0 border-none mt-4"
            tableRootClassName="border-theme-zinc-200 checkout-table"
            loading={loading}
            columns={[
              {
                title: "Course Name",
                dataIndex: "name",
                key: "id",
              },
              {
                title: "Price",
                dataIndex: "price",
                render: (text) => "৳" + enToBnNumber(commaNumber(text)),
                key: "id",
                align: "center",
              },
              {
                title: "Total",
                dataIndex: "total",
                render: (text) => "৳" + enToBnNumber(commaNumber(text)),
                key: "id",
                align: "center",
              },
            ]}
            dataSource={courses}
          />
          {/* Checkbox */}
          <div className="flex items-start font-roboto mt-4">
            <Checkbox
              checked={checked}
              className="mr-3 h-5 w-4"
              id="terms-and-conditions"
              onChange={() => setChecked(!checked)}
            />
            <label
              className="text-title-primary !text-base !font-normal relative -top-0.5 cursor-pointer"
              htmlFor="terms-and-conditions"
            >
              এই ক্রয় সম্পন্ন করার মাধ্যমে আমি নিশ্চিত করছি যে{" "}
              <span className="font-roboto">manPowerX</span>-এর{" "}
              <a
                className="text- hover:underline hover:text-theme-primary underline"
                href="https://upscalebd.com/privacy-policy"
                target="_blank"
              >
                গোপনীয়তা নীতি
              </a>{" "}
              এবং{" "}
              <a
                className="text-inherit hover:underline hover:text-theme-primary underline"
                href="https://upscalebd.com/refund-policy"
                target="_blank"
              >
                রিফান্ড নীতি
              </a>{" "}
              আমি পড়েছি এবং এতে সম্মতি প্রদান করছি।
            </label>
          </div>
        </div>
        <div className="">
          <h2 className="text-2xl font-bold font-roboto text-theme-slate-700">
            Summery
          </h2>
          <div className="w-full p-5 rounded-lg border border-theme-zinc-200 mt-4">
            <div className="flex justify-between py-2 border-b  border-theme-zinc-200 text-theme-slate-700 text-base font-normal">
              <span>Sub Total:</span>
              <span>৳{enToBnNumber(commaNumber(totalPrice))}</span>
            </div>
            <div className="flex justify-between py-2 border-b  border-theme-zinc-200 text-theme-slate-700 text-base font-normal">
              <span>Discount:</span>
              <span
                className={cn(discount > 0 && "text-green-600 font-medium")}
              >
                {discount > 0 ? "-" : ""}৳{enToBnNumber(commaNumber(discount))}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-theme-zinc-200 text-theme-slate-700 text-base font-medium">
              <span>Total Payable:</span>
              <span className="font-medium">
                ৳{enToBnNumber(commaNumber(grandTotal))}
              </span>
            </div>

            <div className="mt-4">
              <Input
                ref={referenceInputRef}
                value={reference}
                onChange={(e) => {
                  setReference(e.target.value);
                  setShowReferenceWarning(false);
                }}
                placeholder="রেফারেন্স"
                className={cn(
                  showReferenceWarning &&
                    "ring-0.5 ring-theme-primary border-theme-primary bg-theme-primary/5 animate-pulse",
                )}
                suffix={
                  <Tooltip title="আপনি আমাদের কোর্স সম্পর্কে কীভাবে জানলেন?">
                    <InfoCircleOutlined
                      className={cn(
                        showReferenceWarning && "text-theme-primary",
                      )}
                    />
                  </Tooltip>
                }
              />
            </div>

            <Tooltip
              title={checked ? "" : "অনুগ্রহ করে শর্তাবলিতে সম্মতি প্রদান করুন"}
            >
              <Button
                type="primary"
                className="rounded-full mt-4"
                block
                disabled={
                  !checked ||
                  !totalPrice ||
                  isAlreadyBuy ||
                  isCourseValidateLoading
                }
                loading={addOrderLoading || publicCheckoutLoading}
                onClick={handlePayment}
              >
                Proceed to Payment
              </Button>
            </Tooltip>

            <Flex align="center" gap={16} className="mt-4">
              <Input
                placeholder="কুপন কোড লিখুন"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
                allowClear
              />
              <Tooltip title={!couponCode && "সাবমিট করার আগে কুপন কোড লিখুন"}>
                <Button
                  className="flex items-center px-2 disabled:bg-white !w-11"
                  disabled={!couponCode}
                  onClick={handleCouponCode}
                  loading={isCourseValidateLoading}
                >
                  {!isCourseValidateLoading && <SendIcon />}
                </Button>
              </Tooltip>
            </Flex>
          </div>

          <p className="justify-start text-slate-400 text-xs font-normal mt-3">
            * All prices are VAT & TAX included
          </p>
        </div>
      </div>

      {/* Confirmation Modal */}
      <Modal
        title="রেফারেন্স যোগ করুন"
        open={showConfirmModal}
        onOk={handleConfirmProceed}
        onCancel={handleCancelProceed}
        okText="রেফারেন্স ছাড়া এগিয়ে যান"
        cancelText="রেফারেন্স যোগ করুন"
        centered
      >
        <p className="text-base">
          আপনি কোনো রেফারেন্স যোগ করেননি। আপনি কি রেফারেন্স ছাড়াই এগিয়ে যেতে
          চান?
        </p>
      </Modal>
    </section>
  );
}
